/**
 * Copyright (c) Qwoach LLC 2020. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 8/25/20
 *
 */
import BaseFormElement from "@/core/modules/coach/Resources/Forms/model/FormElements/BaseFormElement";
import {Type} from "class-transformer";
import FormElementType from "@/core/modules/coach/Resources/Forms/model/FormElements/FormElementType";
import FormElementText from "@/core/modules/coach/Resources/Forms/model/FormElements/General/FormElementText";
import FormElementMultilineText
	from "@/core/modules/coach/Resources/Forms/model/FormElements/General/FormElementMultilineText";
import FormElementRadioButtons
	from "@/core/modules/coach/Resources/Forms/model/FormElements/General/FormElementRadioButtons";
import FormElementCheckboxes
	from "@/core/modules/coach/Resources/Forms/model/FormElements/General/FormElementCheckboxes";

import FormElementTitle from "@/core/modules/coach/Resources/Forms/model/FormElements/Additional/FormElementTitle";
import {FormSpecialFieldEnum} from "@/core/modules/coach/Resources/Forms/model/SpecialFieldEnum";
import {FormTypeEnum} from "@/core/modules/coach/Resources/Forms/model/FormTypeEnum";
import Discriminator from "@/utils/Discriminator";
import FormElementFullName from "@/core/modules/coach/Resources/Forms/model/FormElements/Smart/FormElementFullName";
import FormElementEmailAddress
	from "@/core/modules/coach/Resources/Forms/model/FormElements/Smart/FormElementEmailAddress";
import FormElementFooter from "@/core/modules/coach/Resources/Forms/model/FormElements/Additional/FormElementFooter";
import FormElementScoreResult
	from "@/core/modules/coach/Resources/Forms/model/FormElements/Additional/FormElementScoreResult";
import {FilterableProperty} from "@shared/Filterable/PropertyDecorators";
import FormElementAddress from "@/core/modules/coach/Resources/Forms/model/FormElements/Smart/FormElementAddress";
import FormElementPhoneNumber
	from "@/core/modules/coach/Resources/Forms/model/FormElements/Smart/FormElementPhoneNumber";
import FormElementEmergencyContacts
	from "@/core/modules/coach/Resources/Forms/model/FormElements/Smart/FormElementEmergencyContacts";
import FormElementDateOfBirth
	from "@/core/modules/coach/Resources/Forms/model/FormElements/Smart/FormElementDateOfBirth";
import FormElementContactInfo
	from "@/core/modules/coach/Resources/Forms/model/FormElements/General/FormElementContactInfo";
import FormElementTextGrid from "@/core/modules/coach/Resources/Forms/model/FormElements/General/FormElementTextGrid";
import FormElementDate from "@/core/modules/coach/Resources/Forms/model/FormElements/General/FormElementDate";
import {QwoachFileType} from "@shared/Drive/QwoachFileType";
import {ClearanceLevelEnum} from "@shared/Profile/ClearanceLevelEnum.js";
import FilterableItem from "@shared/Filterable/FilterableItem.js";
import {mergeArraysBySum} from "@/utils/helpers.js";
import FormSettings from "@/core/modules/coach/Resources/Forms/model/FormSettings.js";

export default class FormItem extends FilterableItem {

	@Type(() => BaseFormElement, Discriminator.create([
			{value: FormElementTitle, name: FormElementType.TITLE},
			{value: FormElementFooter, name: FormElementType.FOOTER},
			{value: FormElementText, name: FormElementType.TEXT},
			{value: FormElementMultilineText, name: FormElementType.MULTILINE},
			{value: FormElementCheckboxes, name: FormElementType.CHECKBOXES},
			{value: FormElementRadioButtons, name: FormElementType.RADIOBUTTONS},
			{value: FormElementFullName, name: FormElementType.FULL_NAME},
			{value: FormElementScoreResult, name: FormElementType.SCORE_RESULT},
			{value: FormElementEmailAddress, name: FormElementType.EMAIL_ADDRESS},
			{value: FormElementAddress, name: FormElementType.ADDRESS},
			{value: FormElementDateOfBirth, name: FormElementType.DATE_OF_BIRTH},
			{value: FormElementPhoneNumber, name: FormElementType.PHONE_NUMBER},
			{value: FormElementEmergencyContacts, name: FormElementType.EMERGENCY_CONTACTS},
			{value: FormElementContactInfo, name: FormElementType.CONTACT_INFO},
			{value: FormElementTextGrid, name: FormElementType.TEXT_GRID},
			{value: FormElementDate, name: FormElementType.DATE},

		])
	)
	formElements: BaseFormElement[] = []

	@FilterableProperty()
	title: string = ""

	@FilterableProperty()
	clientName: string = ""

	@FilterableProperty()
	clientEmail: string = ""

	clientId: string = ""
	isEnabled: boolean = true
	disabledMessage: string = "";
	formType: FormTypeEnum = FormTypeEnum.GENERAL

	sessionTypeId: string = ""

	@FilterableProperty()
	sessionTypeName: string = ""

	isEditable: boolean = true
	isIntakeForm: boolean = false

	attachedWorkflowId: string = null
	workflowTitle: string = null

	clearanceLevel: ClearanceLevelEnum = ClearanceLevelEnum.STANDARD

	type: QwoachFileType = QwoachFileType.FORM

	@Type(() => FormSettings)
	settings: FormSettings = new FormSettings()


	constructor() {
		super();
	}

	getCurrentSchemaVersion(){
		return 2
	}

	migrateSchema(oldVersion, newVersion) {
		if(oldVersion < 2){
			this.syncSettingsFromFormElements()
		}
		super.migrateSchema(oldVersion, newVersion);
	}

	syncSettingsFromFormElements(){
		this.settings.resetCollectInfoSettings()

		this.formElements.forEach(formElement => {
			switch (formElement.type) {
				case FormElementType.FULL_NAME:
					this.settings.collectName = true
					break
				case FormElementType.EMAIL_ADDRESS:
					this.settings.collectEmail = true
					break
				case FormElementType.PHONE_NUMBER:
					this.settings.collectPhone = true
					break
				case FormElementType.ADDRESS:
					this.settings.collectAddress = true
					break
				case FormElementType.DATE_OF_BIRTH:
					this.settings.collectDateOfBirth = true
					break
				case FormElementType.EMERGENCY_CONTACTS:
					this.settings.collectEmergencyContacts = true
					break
			}
		})
	}

	syncFormElementsFromSettings(){
		console.log("FormItem.syncFormElementsFromSettings: settings = ", this.settings)
		let map = [
			{setting: "collectName", elementType: FormElementType.FULL_NAME, title: "Full Name", constructor: FormElementFullName},
			{setting: "collectEmail", elementType: FormElementType.EMAIL_ADDRESS, title: "Email", constructor: FormElementEmailAddress},
			{setting: "collectPhone", elementType: FormElementType.PHONE_NUMBER, title: "Phone Number", constructor: FormElementPhoneNumber},
			{setting: "collectAddress", elementType: FormElementType.ADDRESS, title: "Address", constructor: FormElementAddress},
			{setting: "collectDateOfBirth", elementType: FormElementType.DATE_OF_BIRTH, title: "Date of Birth", constructor: FormElementDateOfBirth},
			{setting: "collectEmergencyContacts", elementType: FormElementType.EMERGENCY_CONTACTS, title: "Emergency Contacts", constructor: FormElementEmergencyContacts},
		]

		let position = this.findElementByType(FormElementType.TITLE) ? 1 : 0

		for(let {setting, elementType, title, constructor} of map){
			// console.log("FormItem.syncFormElementsFromSettings: setting = ", setting, ", elementType = ", elementType, ", title = ", title)
			let element = this.findElementByType(elementType)
			console.log("FormItem.syncFormElementsFromSettings: element = ", element, ", this.settings[setting] = ", this.settings[setting], ", setting = ", setting)

			if(this.settings[setting]){
				if(!element){
					let elem = new constructor(title)
					this.formElements.splice(position, 0, elem)
					console.log("FormItem.syncFormElementsFromSettings: added element ", title, ", element = ", elem)
				}
				position++
			} else if(!this.settings[setting] && element){
				this.formElements = this.formElements.filter(elem => elem !== element)
				console.log("FormItem.syncFormElementsFromSettings: removed element ", title, ", element = ", element)
			}
		}
	}

	applySettings(settings){
		console.log("FormItem.applySettings: settings = ", settings)
		this.settings = settings
	}


	getTitle() {
		if (this.sessionTypeName) {
			return this.title + " for the " + this.sessionTypeName
		}
		return this.title
	}

	setFullName(fullName) {
		this.clientName = fullName || ""
		let element = this.findFullNameElement()
		if (element) {
			element.answer = this.clientName
		}
	}

	setEmail(email) {
		this.clientEmail = email || ""
		let element = this.findEmailElement()
		if (element) {
			element.answer = this.clientEmail
		}
	}

	setClientId(clientId) {
		this.clientId = clientId || ""
	}

	fillInClientProfileFromSmartFields(clientProfile) {
		for (let i = 0; i < this.formElements.length; i++) {
			let elem = this.formElements[i]
			let propName = elem.getSmartFiledPropertyName()
			if (propName && elem.getAnswer()) {
				clientProfile[propName] = elem.getAnswer()
			}
		}
		if(clientProfile.email){
			clientProfile.email = clientProfile.email.toLowerCase().trim()
		}

		// if(this.isIntakeForm){
		// 	clientProfile.intakeFormIds[this.formId] = this.id
		// }

	}

	fillInSmartFieldsFromClientProfile(clientProfile) {
		console.log("FormItem.fillInSmartFieldsFromClientProfile: clientProfile = ",clientProfile)
		for (let i = 0; i < this.formElements.length; i++) {
			let elem = this.formElements[i]
			let propName = elem.getSmartFiledPropertyName()
			if (propName && clientProfile[propName]) {
				elem.setAnswer(clientProfile[propName])
			}
		}

		this.clientName = clientProfile.name
		this.clientEmail = clientProfile.email
	}


	formElementsPrimary() {
		return this.formElements.filter(element => !element.isFullNameItem() && !element.isEmailItem() && !element.isTypeTitle() && !element.isTypeFooter())
	}

	findElementByType(elementType) {
		return this.formElements.find(element => element.type === elementType)
	}
	findScoreResultElement() {
		return this.findElementByType(FormElementType.SCORE_RESULT)
	}
	findFullNameElement() {
		return this.formElements.find(element => element.type === FormSpecialFieldEnum.FULL_NAME)
	}

	findEmailElement() {
		return this.formElements.find(element => element.type === FormSpecialFieldEnum.EMAIL)
	}

	findScoreResultsElement() {
		return this.formElements.find(element => element.type === FormElementType.SCORE_RESULT)
	}

	getClientName(){
		if(this.clientName){
			return this.clientName
		}
		let fullNameElement = this.findFullNameElement()
		if(fullNameElement){
			return fullNameElement.answer
		}
	}

	getClientEmail(){
		if(this.clientEmail){
			return this.clientEmail
		}
		let emailElement = this.findEmailElement()
		if(emailElement){
			return emailElement.answer
		}
	}

	isSpecialFormElement(formElement) {
		return formElement.isSpecialFormElement()
	}

	selectFirstVisibleQuestion() {
		for (let i = 0; i < this.formElements.length; i++) {
			let elem = this.formElements[i]
			if (!this.isSpecialFormElement(elem)) {
				elem._isSelected = true
				return
			}
		}
	}

	clearSelection() {
		for (let i = 0; i < this.formElements.length; i++) {
			let elem = this.formElements[i]
			elem._isSelected = false
		}
	}

	hasSelectedElements() {
		for (let i = 0; i < this.formElements.length; i++) {
			let elem = this.formElements[i]
			if (elem._isSelected) {
				return true
			}
		}
		return false
	}

	static defaultDisabledMessage() {
		return "This form is no longer accepting responses"
	}


	static createEmptyFormItem(title = "Untitled Form", options = {}) {

		let formItem = new FormItem()
		formItem.title = title

		let titleElement = new FormElementTitle(title)
		titleElement.showDescription = true

		let nameElement = new FormElementFullName("Full Name")
		nameElement.isRequired = true
		let emailElement = new FormElementEmailAddress("Email")
		emailElement.isRequired = true

		let questionElement = new FormElementRadioButtons("Question 1")
		questionElement.isRequired = true

		if (!options.skipTitle) {
			formItem.formElements.push(titleElement)
		}
		formItem.formElements.push(nameElement)
		formItem.formElements.push(emailElement)
		if (!options.skipExtraQuestion) {
			formItem.formElements.push(questionElement)
		}

		return formItem

	}

	addElement(element, position){
		if(position === undefined){
			this.formElements.push(element)
		} else {
			this.formElements.splice(position, 0, element)
		}
		this.syncSettingsFromFormElements()
		return this
	}

	deleteElement(element){
		this.formElements = this.formElements.filter(elem => elem.id !== element.id)
		this.syncSettingsFromFormElements()
		return this
	}

	onRecordAnswers() {
		this.recalculateScore()
	}

	getScoreTitle() {
		let scoreResultsElement = this.findScoreResultsElement()
		if (scoreResultsElement) {
			return scoreResultsElement.getScoreTitleByScore()
		}
		return ""
	}


	getScoreResultsOrRecalculate() {
		let scoreResultsElement = this.findScoreResultsElement()
		if (scoreResultsElement) {
			if (scoreResultsElement.scoreResultAnswer === null) {
				scoreResultsElement = this.recalculateScore()
			}
			return scoreResultsElement.getScoreValue()
		}
		return null
	}

	recalculateScore() {
		let scoreResultsElement = this.findScoreResultsElement()

		console.log("FormItem.recalculateScore: scoreResultsElement = ", scoreResultsElement)

		if (scoreResultsElement) {
			let resultAsBuckets = scoreResultsElement.resultAsBuckets
			let score = 0
			let bucketScore = []
			this.formElements.forEach(formElement => {
				if (formElement.isScoringEnabled() && formElement.answerOptionsScoring) {
					let scoreLocal = formElement.calculateAnswerScore(this, scoreResultsElement.resultAsBuckets)
					if(resultAsBuckets){
						bucketScore = mergeArraysBySum(bucketScore, scoreLocal)
					} else {
						score += scoreLocal
					}
					console.log("FormItem.recalculateScore: scoreLocal = ", scoreLocal, ", bucketScore = ", bucketScore)

					// if(formElement.selectedIndex >= 0){
					// 	scoreLocal += formElement.answerOptionsScoring[formElement.selectedIndex]
					// } else if (formElement.checkboxAnswers) {
					// 	formElement.checkboxAnswers.forEach((checkboxAnswer, index) => {
					// 		if (checkboxAnswer) {
					// 			scoreLocal += formElement.answerOptionsScoring[index]
					// 		}
					// 	})
					// }
					// score += scoreLocal
				}
			})
			scoreResultsElement.scoreResultAnswer = score
			scoreResultsElement.bucketResultAnswer = bucketScore
		}
		return scoreResultsElement
	}

	getWorkflowTitle(){
		if(this.attachedWorkflowId && !this.workflowTitle){
			return "IAOTRC Workflow"
		}
		return this.workflowTitle
	}

	toAnswersArray() {
		let answers = []
		this.formElements.forEach(formElement => {
			answers.push({
				question: formElement.title,
				answer: formElement.getAnswer()
			})
		})
		return answers
	}

}
